import React from 'react';
import { keyBy } from 'lodash';
import {
    InProgressStatus,
    ClearStatus,
    IssuesFoundStatus,
    RequestedStatus,
    RequestExpiredStatus,
    LowRiskStatus,
    HighRiskStatus,
} from '@fiverr-private/statuses';
import { translate } from '@fiverr-private/i18n-react';
import { DocumentStatus, SellerLegalDocsStats } from '@fiverr-private/legal_documents_package';
import { Link } from '@fiverr-private/typography';
import {
    BackgroundCheckDataType,
    ClassificationRequestResults,
    ComplianceStatusRank,
    RequestResults,
    RequestStatuses,
    WorkforceClassificationDataType,
    WorkforceClassificationRequestStatuses,
} from '../../types/complianceTypes';

export function backgroundCheckStatusRank(backgroundCheckData?: BackgroundCheckDataType): ComplianceStatusRank {
    if (!backgroundCheckData) {
        return ComplianceStatusRank.UNDEFINED;
    }

    const requestStatus = backgroundCheckData?.requestStatus;
    const validRequestStatuses = [
        RequestStatuses.COMPLETED,
        RequestStatuses.IN_PROGRESS,
        RequestStatuses.REQUESTED,
        RequestStatuses.EXPIRED,
        RequestStatuses.FAILED,
    ];

    if (!validRequestStatuses.includes(requestStatus)) {
        return ComplianceStatusRank.UNDEFINED;
    }

    const isBlueStatuses = [RequestStatuses.IN_PROGRESS, RequestStatuses.REQUESTED].includes(requestStatus);
    if (isBlueStatuses) return ComplianceStatusRank.BLUE;

    if ([RequestStatuses.EXPIRED, RequestStatuses.FAILED].includes(requestStatus)) return ComplianceStatusRank.YELLLOW;

    const requestResults = backgroundCheckData?.requestResults;

    if (!requestResults) {
        return ComplianceStatusRank.UNDEFINED;
    }

    if (requestResults === RequestResults.CLEAR) return ComplianceStatusRank.GREEN;

    if ([RequestResults.CONSIDER, RequestResults.DISPUTE].includes(requestResults)) {
        return ComplianceStatusRank.YELLLOW;
    }

    return ComplianceStatusRank.UNDEFINED;
}

export function workforceClassificationRank(
    classificationRequestData?: WorkforceClassificationDataType
): ComplianceStatusRank {
    if (!classificationRequestData) {
        return ComplianceStatusRank.UNDEFINED;
    }

    const requestStatus = classificationRequestData?.requestStatus;
    const validRequestStatuses = [
        WorkforceClassificationRequestStatuses.COMPLETED,
        WorkforceClassificationRequestStatuses.IN_PROGRESS,
        WorkforceClassificationRequestStatuses.REQUESTED,
        WorkforceClassificationRequestStatuses.EXPIRED,
        WorkforceClassificationRequestStatuses.CANCELED,
    ];

    if (!validRequestStatuses.includes(requestStatus)) {
        return ComplianceStatusRank.UNDEFINED;
    }

    const isBlueStatuses = [
        WorkforceClassificationRequestStatuses.IN_PROGRESS,
        WorkforceClassificationRequestStatuses.REQUESTED,
    ].includes(requestStatus);
    if (isBlueStatuses) return ComplianceStatusRank.BLUE;

    if (
        [WorkforceClassificationRequestStatuses.EXPIRED, WorkforceClassificationRequestStatuses.CANCELED].includes(
            requestStatus
        )
    ) {
        return ComplianceStatusRank.YELLLOW;
    }

    const requestResults = classificationRequestData?.requestResults;

    if (!requestResults) {
        return ComplianceStatusRank.UNDEFINED;
    }

    if (requestResults === ClassificationRequestResults.INDEPENDENT_CONTRACTOR) {
        return ComplianceStatusRank.GREEN;
    }

    if (requestResults === ClassificationRequestResults.EMPLOYEE) {
        return ComplianceStatusRank.YELLLOW;
    }

    return ComplianceStatusRank.UNDEFINED;
}

export function getLegalDocumentsRank(sellerLegalDocsData?: SellerLegalDocsStats): ComplianceStatusRank {
    if (!sellerLegalDocsData) {
        return ComplianceStatusRank.UNDEFINED;
    }
    const statusesStats = sellerLegalDocsData?.statuses;
    const documentsByStatus = keyBy(statusesStats, 'status');
    const allDocumentsCount = statusesStats?.reduce((acc, stat) => acc + stat?.count, 0);

    if (documentsByStatus[DocumentStatus.EXPIRED]?.count > 0) {
        return ComplianceStatusRank.YELLLOW;
    }

    if (documentsByStatus[DocumentStatus.SIGNED]?.count === allDocumentsCount) {
        return ComplianceStatusRank.GREEN;
    }

    return ComplianceStatusRank.BLUE;
}

export function getBackgroundCheckStatus(backgroundCheckData?: BackgroundCheckDataType) {
    if (!backgroundCheckData) {
        return null;
    }

    const backgroundCheckStatus = backgroundCheckData?.requestStatus;
    switch (backgroundCheckStatus) {
        case RequestStatuses.REQUESTED:
            return <RequestedStatus />;
        case RequestStatuses.IN_PROGRESS:
            return <InProgressStatus />;
        case RequestStatuses.COMPLETED:
            const resultValue = backgroundCheckData?.requestResults;
            if (!resultValue) {
                return null;
            }
            return resultValue === RequestResults.CLEAR ? <ClearStatus /> : <IssuesFoundStatus />;
        case RequestStatuses.EXPIRED:
        case RequestStatuses.FAILED:
            return <RequestExpiredStatus />;
        default:
            return null;
    }
}

export function getBackgroundCheckStatusTooltip(sellerUserName: string, backgroundCheckData?: BackgroundCheckDataType) {
    if (!backgroundCheckData) {
        return '';
    }
    const tooltipPrefix = 'compliance.compliance_section.background_check.status_tooltip';

    const backgroundCheckStatus = backgroundCheckData?.requestStatus;
    switch (backgroundCheckStatus) {
        case RequestStatuses.IN_PROGRESS:
            return translate(`${tooltipPrefix}.in_progress`);
        case RequestStatuses.COMPLETED:
            const resultValue = backgroundCheckData?.requestResults;
            if (!resultValue) {
                return null;
            }
            if (resultValue === RequestResults.CLEAR) {
                return translate(`${tooltipPrefix}.clear`);
            } else {
                return translate(`${tooltipPrefix}.issue_found`);
            }
        case RequestStatuses.EXPIRED:
        case RequestStatuses.FAILED:
            return translate(`${tooltipPrefix}.expired`, {
                params: {
                    sellerUserName,
                },
            });
        default:
            return '';
    }
}

export function getWorkforceClassificationStatus(classificationRequestData?: WorkforceClassificationDataType) {
    if (!classificationRequestData) {
        return null;
    }

    const workforceClassificationStatus = classificationRequestData?.requestStatus;
    switch (workforceClassificationStatus) {
        case WorkforceClassificationRequestStatuses.REQUESTED:
            return <RequestedStatus />;
        case WorkforceClassificationRequestStatuses.IN_PROGRESS:
            return <InProgressStatus />;
        case WorkforceClassificationRequestStatuses.COMPLETED:
            const resultValue = classificationRequestData?.requestResults;
            if (!resultValue) {
                return null;
            }
            return resultValue === ClassificationRequestResults.INDEPENDENT_CONTRACTOR ? (
                <LowRiskStatus />
            ) : (
                <HighRiskStatus />
            );
        case WorkforceClassificationRequestStatuses.EXPIRED:
        case WorkforceClassificationRequestStatuses.CANCELED:
            return <RequestExpiredStatus />;
        default:
            return null;
    }
}

export function getWorkforceClassificationStatusTooltip(
    sellerUserName: string,
    classificationRequestData?: WorkforceClassificationDataType
) {
    if (!classificationRequestData) {
        return '';
    }
    const tooltipPrefix = 'compliance.compliance_section.classification.status_tooltip';

    const workforceClassificationStatus = classificationRequestData?.requestStatus;
    switch (workforceClassificationStatus) {
        case WorkforceClassificationRequestStatuses.IN_PROGRESS:
            return translate(`${tooltipPrefix}.in_progress`);
        case WorkforceClassificationRequestStatuses.COMPLETED:
            const resultValue = classificationRequestData?.requestResults;
            if (!resultValue) {
                return '';
            }
            if (resultValue === ClassificationRequestResults.INDEPENDENT_CONTRACTOR) {
                return translate(`${tooltipPrefix}.low_risk`, {
                    params: {
                        sellerUserName,
                    },
                });
            } else {
                return translate(`${tooltipPrefix}.high_risk`, {
                    params: {
                        sellerUserName,
                    },
                    templates: {
                        link: (text: string | undefined) => (
                            <Link
                                href="https://help-pro.fiverr.com/hc/en-us/articles/30655018603665-Addressing-workforce-classification-compliance-risks"
                                target="_blank"
                                rel="noopener noreferrer"
                                color="white"
                            >
                                {text}
                            </Link>
                        ),
                    },
                });
            }
        case WorkforceClassificationRequestStatuses.EXPIRED:
        case WorkforceClassificationRequestStatuses.CANCELED:
            return translate(`${tooltipPrefix}.expired`, {
                params: {
                    sellerUserName,
                },
            });
        default:
            return '';
    }
}
